import React, { useMemo, useState } from "react";
import {
  Card,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Images } from "../../utils/Images";
import { SHARE_URL_BUILDERS, withShareTracking } from "../../utils/socialShare";

const SHARE_ICON_CONFIG = {
  whatsapp: { name: "Whatsapp", icon: Images.whatsappIcon },
  facebook: { name: "Facebook", icon: Images.facebookIcon },
  twitter: { name: "X", icon: Images.xicon },
  linkedin: { name: "Linkedin", icon: Images.linkedinIcon },
};

const FALLBACK_SHARE_BUILDERS = [
  { key: "whatsapp", name: "Whatsapp", icon: Images.whatsappIcon },
  { key: "facebook", name: "Facebook", icon: Images.facebookIcon },
  { key: "twitter", name: "X", icon: Images.xicon },
  { key: "linkedin", name: "Linkedin", icon: Images.linkedinIcon },
  { key: "gmail", name: "Gmail", icon: Images.gmailIcon },
].map((option) => ({ ...option, buildLink: SHARE_URL_BUILDERS[option.key] }));

const ShareLink = ({
  shareData,
  fallbackUrl = "",
  isLoading = false,
  error,
  onRetry,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [copied, setCopied] = useState(false);

  const shareUrl =
    shareData?.share_links?.copy || shareData?.link || fallbackUrl || "";
  const shareMessage = shareData?.message || "";
  // What the donor sees and copies; the platform buttons tag their own links.
  const copyUrl = useMemo(() => withShareTracking(shareUrl, "copy"), [shareUrl]);

  const derivedShareOptions = useMemo(() => {
    if (!shareData?.share_links) {
      return [];
    }

    return Object.entries(SHARE_ICON_CONFIG).reduce((acc, [key, config]) => {
      const url = shareData.share_links[key];
      if (url) {
        acc.push({
          name: config.name,
          icon: config.icon,
          link: url,
        });
      }
      return acc;
    }, []);
  }, [shareData]);

  const fallbackShareOptions = useMemo(() => {
    if (!shareUrl) {
      return [];
    }

    return FALLBACK_SHARE_BUILDERS.map((option) => ({
      name: option.name,
      icon: option.icon,
      link: option.buildLink(shareUrl, shareMessage),
    }));
  }, [shareUrl, shareMessage]);

  const shareOptions =
    derivedShareOptions.length > 0 ? derivedShareOptions : fallbackShareOptions;

  const shouldShowLoader = isLoading && !shareData && !shareUrl;

  const handleCopy = async () => {
    if (!copyUrl) return;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(copyUrl);
      } else {
        // Fallback for older browsers/mobile
        const textArea = document.createElement('textarea');
        textArea.value = copyUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Failed to copy:', err);
        }
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Still show copied message even if it fails
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = (link) => {
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      sx={{
        p: isMobile ? "12px" : "30px",
        borderRadius: isMobile ? "12px" : 3,
        boxShadow: 4,
        maxWidth: isMobile ? "100%" : "745px",
        minWidth: isMobile ? "340px" : "auto",
        margin: "0 auto",
        width: isMobile ? "100%" : "90%",
        minHeight: isMobile ? "180px" : "auto",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: isMobile ? 1.5 : 2,
        }}
      >
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ 
            color: "#0A0A23", 
            fontSize: isMobile ? "16px" : "24px",
            lineHeight: isMobile ? "20px" : "auto",
          }}
        >
          Share the link
        </Typography>
        <IconButton 
          onClick={onClose}
          sx={{ 
            padding: isMobile ? "4px" : "8px",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
          }}
        >
          <Box
            component="img"
            src={Images.closeIcon}
            alt="Close"
            sx={{ 
              width: isMobile ? 20 : 24, 
              height: isMobile ? 20 : 24, 
              cursor: "pointer" 
            }}
          />
        </IconButton>
      </Box>

      {shouldShowLoader ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: isMobile ? "120px" : 180,
          }}
        >
          <CircularProgress size={isMobile ? 24 : 32} />
        </Box>
      ) : (
        <>
          {error && (
            <Box
              sx={{
                backgroundColor: "#FDECEC",
                borderRadius: isMobile ? "8px" : "10px",
                p: isMobile ? 1.5 : 2,
                mb: isMobile ? 1.5 : 2,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ 
                  color: "#B42318", 
                  fontWeight: 500,
                  fontSize: isMobile ? "12px" : "14px",
                  lineHeight: isMobile ? "16px" : "auto",
                }}
              >
                {error}
              </Typography>
              {onRetry && (
                <Button
                  size="small"
                  onClick={onRetry}
                  sx={{ 
                    color: "#B42318", 
                    textTransform: "none",
                    fontSize: isMobile ? "12px" : "14px",
                    minWidth: isMobile ? "auto" : "64px",
                    padding: isMobile ? "4px 8px" : "6px 16px",
                  }}
                >
                  Retry
                </Button>
              )}
            </Box>
          )}

          {shareMessage && (
            <Typography
              variant="body2"
              sx={{
                color: "#4A4A49",
                fontSize: isMobile ? "12px" : "16px",
                fontWeight: 500,
                mb: isMobile ? 1.5 : 2,
                lineHeight: isMobile ? "16px" : "auto",
              }}
            >
              {shareMessage}
            </Typography>
          )}

          <Box 
            display="flex" 
            mb={isMobile ? 2 : 3}
            sx={{
              flexDirection: isMobile ? "row" : "row",
              gap: isMobile ? 0.5 : 0,
              alignItems: "stretch",
            }}
          >
            <TextField
              fullWidth
              value={copyUrl}
              size={isMobile ? "small" : "medium"}
              placeholder="https://truehopefoundation..."
              variant="outlined"
              InputProps={{
                sx: {
                  backgroundColor: "#F5F8ED",
                  borderRadius: isMobile ? "8px 0 0 8px" : "13px",
                  fontSize: isMobile ? "11px" : "14px",
                  fontWeight: 500,
                  color: "#686866",
                  height: isMobile ? "36px" : "auto",
                  "& fieldset": { border: "none" },
                  "& input": {
                    padding: isMobile ? "8px 10px" : "14px",
                    fontSize: isMobile ? "11px" : "14px",
                  },
                },
                readOnly: true,
              }}
            />
            <Button
              onClick={handleCopy}
              variant="contained"
              disabled={!shareUrl}
              sx={{
                ml: isMobile ? 0 : 1,
                borderRadius: isMobile ? "0 8px 8px 0" : "8px",
                backgroundColor: "#FF5858",
                color: "#fff",
                fontSize: isMobile ? "11px" : "16px",
                fontWeight: 700,
                width: isMobile ? "80px" : "119px",
                height: isMobile ? "36px" : "auto",
                textAlign: "center",
                px: isMobile ? 0.5 : 2,
                minWidth: isMobile ? "80px" : "119px",
                flexShrink: 0,
                "&:hover": { backgroundColor: "#e14b4b" },
                opacity: shareUrl ? 1 : 0.6,
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </Box>

          <Box display="flex" alignItems="center" mb={isMobile ? 1.5 : 2}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              variant="body2"
              sx={{
                mx: 1,
                color: "#4A4A49",
                fontSize: isMobile ? "12px" : "18px",
                fontWeight: 500,
                px: isMobile ? "8px" : "20px",
                whiteSpace: "nowrap",
              }}
            >
              Share via
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {shareOptions.length ? (
            <Grid
              container
              pt={isMobile ? 1 : 3}
              sx={{
                justifyContent: isMobile ? "space-between" : "space-around",
                gap: isMobile ? "8px" : "30px",
                rowGap: isMobile ? "16px" : "30px",
              }}
            >
              {shareOptions.map((option) => (
                <Grid
                  item
                  key={option.name}
                  xs={isMobile ? 2.4 : 4}
                  sm={3}
                  textAlign="center"
                  onClick={() => handleShare(option.link)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: option.link ? "pointer" : "not-allowed",
                    opacity: option.link ? 1 : 0.5,
                    minWidth: isMobile ? "60px" : "auto",
                  }}
                >
                  <Box
                    component="img"
                    src={option.icon}
                    alt={option.name}
                    sx={{ 
                      width: isMobile ? 32 : 40, 
                      height: isMobile ? 32 : 40,
                      objectFit: "contain",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: isMobile ? "10px" : "16px",
                      fontWeight: 500,
                      color: "#4A4A49",
                      pt: isMobile ? 0.5 : 1,
                      lineHeight: isMobile ? "12px" : "auto",
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {option.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#4A4A49",
                fontWeight: 500,
                py: 2,
              }}
            >
              Share links will be available soon.
            </Typography>
          )}
        </>
      )}
    </Card>
  );
};

export default ShareLink;
